import { RequestScope } from '../actions/api';
import {
  BulkSurveysFormData,
  MergeOption,
  SurveyTemplate,
  SurveyTemplateMinData,
  TemplateHistory,
  TemplateTableStats,
} from '@features/survey-templates/types';
import { Action } from '../constants/action';
import { g17ecoApi, transformResponse } from './g17ecoApi';
import { ApiResponse } from '../types/api';
import { SurveyPermission } from '../types/permission';
import { SurveyWorkgroup } from '@g17eco/types/workgroup';

export enum TemplateTag {
  SurveyTemplates = 'survey-templates',
  TemplateWorkgroups = 'template-workgroups',
}

const getTemplateWorkgroupsUrl = (templateId: string) => `/survey-templates/${templateId}/workgroups`;

export type DelegateTemplateWorkgroupParams = {
  templateId: string;
  workgroupIds: string[];
  permission: Pick<SurveyPermission, 'access' | 'scope' | 'roles'>;
};

export type UpdateTemplateWorkgroupParams = Pick<DelegateTemplateWorkgroupParams, 'templateId' | 'permission'> & {
  workgroupId: string;
};

type TemplateCreateMutationParams = Omit<
  SurveyTemplateMinData,
  '_id' | 'evidenceRequired' | 'noteRequired' | 'verificationRequired' | 'type'
> & {
  evidenceRequired?: boolean;
  noteRequired?: boolean;
  verificationRequired?: boolean;
};

type TemplateUpdateMutationParams = Partial<Omit<SurveyTemplateMinData, 'scope' | 'initiativeId'>>;

interface TemplateScopeMutationParams {
  templateId: string;
  action: Action;
  scopeGroups: RequestScope[];
}

interface BulkSurveysCreateMutationParams extends BulkSurveysFormData {
  effectiveDate: Date;
}

export const surveyTemplatesApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: Object.values(TemplateTag),
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getTemplates: builder.query<TemplateTableStats[], string>({
        transformResponse: (response: ApiResponse<TemplateTableStats[]>) => response.data,
        query: (initiativeId) => ({
          url: `/initiatives/${initiativeId}/survey-templates/`,
          method: 'get',
        }),
        providesTags: [TemplateTag.SurveyTemplates],
      }),
      getTemplateById: builder.query<TemplateTableStats, string>({
        transformResponse: (response: ApiResponse<TemplateTableStats>) => response.data,
        query: (templateId) => ({
          url: `/survey-templates/${templateId}`,
          method: 'get',
        }),
        providesTags: [TemplateTag.SurveyTemplates],
      }),
      createTemplate: builder.mutation<SurveyTemplate, TemplateCreateMutationParams>({
        invalidatesTags: [TemplateTag.SurveyTemplates],
        transformResponse: (response: ApiResponse<SurveyTemplate>) => response.data,
        query: (data) => ({
          url: '/survey-templates/',
          method: 'post',
          data,
        }),
      }),
      updateTemplate: builder.mutation<SurveyTemplate, TemplateUpdateMutationParams>({
        invalidatesTags: [TemplateTag.SurveyTemplates],
        transformResponse: (response: ApiResponse<SurveyTemplate>) => response.data,
        query: (data) => {
          const { _id: templateId, ...rest } = data;
          return {
            url: `/survey-templates/${templateId}`,
            method: 'patch',
            data: rest,
          };
        },
      }),
      deleteTemplate: builder.mutation<SurveyTemplate, string>({
        invalidatesTags: [TemplateTag.SurveyTemplates],
        transformResponse: (response: ApiResponse<SurveyTemplate>) => response.data,
        query: (templateId) => {
          return {
            url: `/survey-templates/${templateId}`,
            method: 'delete',
          };
        },
      }),
      duplicateTemplate: builder.mutation<SurveyTemplate, string>({
        invalidatesTags: [TemplateTag.SurveyTemplates],
        transformResponse: (response: ApiResponse<SurveyTemplate>) => response.data,
        query: (templateId) => {
          return {
            url: `/survey-templates/${templateId}/duplicate`,
            method: 'post',
            data: { templateId },
          };
        },
      }),
      updateTemplateScope: builder.mutation<SurveyTemplate, TemplateScopeMutationParams>({
        invalidatesTags: [TemplateTag.SurveyTemplates],
        transformResponse: (response: ApiResponse<SurveyTemplate>) => response.data,
        query: (data) => {
          const { templateId, ...rest } = data;
          return {
            url: `/survey-templates/${templateId}/scope`,
            method: 'patch',
            data: rest,
          };
        },
      }),
      bulkSurveysCreate: builder.mutation<TemplateHistory, BulkSurveysCreateMutationParams>({
        invalidatesTags: [TemplateTag.SurveyTemplates],
        transformResponse: (response: ApiResponse<TemplateHistory>) => response.data,
        query: (data) => ({
          url: `/survey-templates/${data._id}/bulk-surveys-create-job`,
          method: 'post',
          data,
        }),
      }),
      getMergeTags: builder.query<MergeOption[], void>({
        transformResponse: (response: ApiResponse<MergeOption[]>) => response.data,
        query: () => ({
          url: '/survey-templates/merge-tags',
          method: 'get',
        }),
        providesTags: [TemplateTag.SurveyTemplates],
      }),
      getTemplateWorkgroups: builder.query<SurveyWorkgroup[], string>({
        transformResponse,
        query: (templateId) => ({
          url: getTemplateWorkgroupsUrl(templateId),
          method: 'get',
        }),
        providesTags: (_result, _error, templateId) => {
          return [{ type: TemplateTag.TemplateWorkgroups, id: templateId }];
        },
      }),
      delegateTemplateWorkgroup: builder.mutation<SurveyPermission[], DelegateTemplateWorkgroupParams>({
        invalidatesTags: (_result, _error, { templateId }) => [
          { type: TemplateTag.TemplateWorkgroups, id: templateId },
        ],
        transformResponse,
        query: ({ templateId, ...data }) => ({
          url: getTemplateWorkgroupsUrl(templateId),
          method: 'post',
          data,
        }),
      }),
      updateTemplateWorkgroup: builder.mutation<SurveyPermission[], UpdateTemplateWorkgroupParams>({
        invalidatesTags: (_result, _error, { templateId }) => [
          { type: TemplateTag.TemplateWorkgroups, id: templateId },
        ],
        transformResponse,
        query: ({ templateId, ...data }) => ({
          url: getTemplateWorkgroupsUrl(templateId),
          method: 'put',
          data,
        }),
      }),
      removeTemplateWorkgroup: builder.mutation<SurveyPermission[], { templateId: string; workgroupId: string }>({
        invalidatesTags: (_result, _error, { templateId }) => [
          { type: TemplateTag.TemplateWorkgroups, id: templateId },
        ],
        transformResponse,
        query: ({ templateId, workgroupId }) => ({
          url: getTemplateWorkgroupsUrl(templateId),
          method: 'delete',
          data: { workgroupIds: [workgroupId] },
        }),
      }),
    }),
  });

export const {
  useGetTemplatesQuery,
  useGetTemplateByIdQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useDuplicateTemplateMutation,
  useUpdateTemplateScopeMutation,
  useBulkSurveysCreateMutation,
  useGetMergeTagsQuery,
  useGetTemplateWorkgroupsQuery,
  useDelegateTemplateWorkgroupMutation,
  useUpdateTemplateWorkgroupMutation,
  useRemoveTemplateWorkgroupMutation,
} = surveyTemplatesApi;
